/**
 * Blocks — the global (shared) section library.
 *
 * A block is one section stored once and referenced from any page via the
 * editor's Add-section palette ("Global blocks" group). Edits here fan out
 * to every page using the block on the next Publish — so saves are an
 * explicit button, not autosave.
 *
 * Roles: editing content is open to all admin roles; creating and deleting
 * blocks is structural → dev-only (same doctrine as page layout).
 */

import { useMemo, useState } from 'react';
import {
  SECTION_DEFAULTS,
  SECTION_META,
  SECTION_SCHEMAS,
  SECTION_TYPES,
  type SectionType,
} from '@vv/sections';
import { SectionForm } from '~/lib/formgen';
import { useAuth } from '~/lib/useAuth';
import { useGlobalBlocks, type GlobalBlockInfo } from '~/lib/useGlobalBlocks';

function timeAgo(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const CARD: React.CSSProperties = {
  background: 'var(--card)',
  border: '1px solid var(--line-soft)',
  borderRadius: 12,
  padding: 16,
};

export default function BlocksPage() {
  const gb = useGlobalBlocks();
  const { user } = useAuth();
  const isDev = user?.role === 'dev';

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Local draft of the selected block (explicit save).
  const [draftName, setDraftName] = useState('');
  const [draftSection, setDraftSection] = useState<Record<string, unknown> | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Create flow
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<SectionType>('cta-panel');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const selected = useMemo(
    () => gb.blocks.find((b) => b.id === selectedId) ?? null,
    [gb.blocks, selectedId],
  );

  function open(block: GlobalBlockInfo) {
    setSelectedId(block.id);
    setDraftName(block.name);
    setDraftSection(JSON.parse(JSON.stringify(block.section)) as Record<string, unknown>);
    setSaveState('idle');
    setSaveError(null);
  }

  const draftIssues = useMemo(() => {
    if (!draftSection) return [];
    const schema = SECTION_SCHEMAS[draftSection.type as SectionType];
    if (!schema) return ['Unknown section type'];
    const parsed = schema.safeParse(draftSection);
    return parsed.success
      ? []
      : parsed.error.issues.slice(0, 4).map((i) => `${i.path.join('.')}: ${i.message}`);
  }, [draftSection]);

  const dirty = useMemo(() => {
    if (!selected || !draftSection) return false;
    return (
      draftName !== selected.name ||
      JSON.stringify(draftSection) !== JSON.stringify(selected.section)
    );
  }, [selected, draftName, draftSection]);

  async function save() {
    if (!selected || !draftSection || draftIssues.length > 0 || !draftName.trim()) return;
    setSaveState('saving');
    setSaveError(null);
    const r = await gb.update(selected.id, { name: draftName.trim(), section: draftSection });
    if (r.ok) {
      setSaveState('saved');
      window.setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 2500);
    } else {
      setSaveState('error');
      setSaveError(r.error ?? 'Save failed');
    }
  }

  async function createBlock() {
    if (!newName.trim()) return;
    setCreateBusy(true);
    setCreateError(null);
    const section = JSON.parse(JSON.stringify(SECTION_DEFAULTS[newType])) as Record<string, unknown>;
    const r = await gb.create(newName.trim(), section);
    setCreateBusy(false);
    if (r.ok && r.id) {
      setCreating(false);
      setNewName('');
      const created = { id: r.id, name: newName.trim(), section, updated_at: new Date().toISOString(), usedBy: [] };
      open(created);
    } else {
      setCreateError(r.error ?? 'Could not create the block');
    }
  }

  if (gb.error) {
    return <div className="content-editor__error">Couldn't load blocks: {gb.error}</div>;
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 320px) 1fr', gap: 16, alignItems: 'start' }}>
      {/* ── Left: block list ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 800, color: 'var(--primary)', margin: 0 }}>
            Blocks
          </h1>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>shared sections</span>
        </div>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          One block, many pages. Edit it here and every page using it updates on the next
          Publish. Insert blocks from the page editor's “Add section” palette.
        </p>

        {isDev &&
          (creating ? (
            <div style={{ ...CARD, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input
                autoFocus
                placeholder="Block name (e.g. Audit CTA)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                style={{ fontSize: 13, padding: '7px 10px', border: '1px solid var(--line)', borderRadius: 8 }}
              />
              <select
                value={newType}
                onChange={(e) => setNewType(e.target.value as SectionType)}
                style={{ fontSize: 13, padding: '7px 10px', border: '1px solid var(--line)', borderRadius: 8, background: 'var(--card)' }}
              >
                {SECTION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {SECTION_META[t].label}
                  </option>
                ))}
              </select>
              {createError && (
                <p style={{ margin: 0, fontSize: 12, color: '#c44545' }}>{createError}</p>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="content-editor__save" disabled={createBusy || !newName.trim()} onClick={() => void createBlock()}>
                  {createBusy ? 'Creating…' : 'Create block'}
                </button>
                <button className="content-editor__rebuild" onClick={() => setCreating(false)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setCreating(true)}
              style={{
                background: 'transparent',
                border: '1px dashed var(--line)',
                color: 'var(--teal)',
                fontWeight: 600,
                borderRadius: 8,
                padding: '9px 10px',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              + New block
            </button>
          ))}

        {gb.loading && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading blocks…</p>}
        {!gb.loading && gb.blocks.length === 0 && (
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            No blocks yet.{isDev ? ' Create one above — a shared CTA panel is the classic first block.' : ''}
          </p>
        )}

        {gb.blocks.map((b) => (
          <button
            key={b.id}
            onClick={() => open(b)}
            style={{
              ...CARD,
              textAlign: 'left',
              cursor: 'pointer',
              border: selectedId === b.id ? '1px solid var(--teal)' : '1px solid var(--line-soft)',
              background: selectedId === b.id ? 'rgba(95,168,162,0.08)' : 'var(--card)',
              padding: '10px 14px',
            }}
          >
            <span style={{ fontWeight: 600, display: 'block', fontSize: 14 }}>{b.name}</span>
            <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
              {SECTION_META[b.section.type as SectionType]?.label ?? String(b.section.type)} ·{' '}
              {b.usedBy.length === 0
                ? 'not on any page yet'
                : `on ${b.usedBy.length} page${b.usedBy.length === 1 ? '' : 's'}`}{' '}
              · {timeAgo(b.updated_at)}
            </span>
          </button>
        ))}
      </div>

      {/* ── Right: editor ── */}
      <div style={CARD}>
        {!selected || !draftSection ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 13.5, margin: 0 }}>
            Select a block to edit it{isDev ? ', or create a new one' : ''}.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                aria-label="Block name"
                style={{
                  flex: 1,
                  minWidth: 200,
                  fontSize: 15,
                  fontWeight: 600,
                  padding: '8px 12px',
                  border: '1px solid var(--line)',
                  borderRadius: 8,
                }}
              />
              {saveState === 'saved' && <span style={{ fontSize: 12, color: 'var(--teal)' }}>✓ Saved</span>}
              {saveState === 'error' && (
                <span style={{ fontSize: 12, color: '#c44545' }} title={saveError ?? ''}>
                  ⚠ {saveError ?? 'Save failed'}
                </span>
              )}
              {dirty && saveState !== 'saving' && draftIssues.length === 0 && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Unsaved changes</span>
              )}
              <button
                className="content-editor__save"
                disabled={saveState === 'saving' || !dirty || draftIssues.length > 0 || !draftName.trim()}
                onClick={() => void save()}
              >
                {saveState === 'saving' ? 'Saving…' : 'Save block'}
              </button>
            </div>

            {selected.usedBy.length > 0 && (
              <p
                style={{
                  margin: 0,
                  padding: '8px 12px',
                  background: 'rgba(95,168,162,0.08)',
                  border: '1px solid rgba(95,168,162,0.3)',
                  borderRadius: 8,
                  fontSize: 12.5,
                }}
              >
                Saving updates <strong>{selected.usedBy.map((p) => `/${p}`).join(', ')}</strong> on
                the next Publish.
              </p>
            )}

            {draftIssues.length > 0 && (
              <div
                style={{
                  padding: '8px 12px',
                  background: 'rgba(220,80,80,0.08)',
                  border: '1px solid rgba(220,80,80,0.3)',
                  borderRadius: 8,
                  fontSize: 12.5,
                  color: '#c44545',
                }}
              >
                <strong>Fix before saving:</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {draftIssues.map((iss, i) => (
                    <li key={i}>{iss}</li>
                  ))}
                </ul>
              </div>
            )}

            <SectionForm
              key={selected.id}
              type={draftSection.type as SectionType}
              value={draftSection}
              onChange={(next) => setDraftSection(next)}
            />

            {isDev && (
              <button
                onClick={async () => {
                  if (selected.usedBy.length > 0) {
                    window.alert(`Still used on: ${selected.usedBy.map((p) => `/${p}`).join(', ')}. Remove it from those pages first.`);
                    return;
                  }
                  if (!window.confirm(`Delete the block "${selected.name}"? This can't be undone.`)) return;
                  const r = await gb.remove(selected.id);
                  if (r.ok) {
                    setSelectedId(null);
                    setDraftSection(null);
                  } else {
                    setSaveState('error');
                    setSaveError(r.error ?? 'Delete failed');
                  }
                }}
                style={{
                  alignSelf: 'flex-start',
                  background: 'transparent',
                  border: '1px solid rgba(196,69,69,0.4)',
                  color: '#c44545',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: 12.5,
                  cursor: 'pointer',
                }}
              >
                Delete block
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
